import { useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import NotificationToast from "../../components/NotificationToast/NotificationToast";
import "bootstrap/dist/css/bootstrap.min.css";
import "./SaveSubmitButtons.css";

export interface SaveSubmitButtonsProps {
  onSave?: () => void | Promise<{ show?: boolean; message?: string; type?: string; reload?: boolean } | void>;
  onSubmit?: () => void | Promise<{ show?: boolean; message?: string; type?: string; reload?: boolean } | void>;
  onAdequacaoSubmit?: () => Promise<{ show?: boolean; message?: string; type?: string; reload?: boolean } | void>;
  onBack?: () => void;
  onCancel?: () => void | Promise<void>;
  onEdit?: () => void;
  podeEditar?: boolean;
  temAdequacao?: boolean;
  showBack?: boolean;
  showCancel?: boolean;
  showEdit?: boolean;
  showSave?: boolean;
  showSubmit?: boolean;
  submitLabel?: string;
  submitDisabled?: boolean;
  onlySubmit?: boolean;
}

const SaveSubmitButtons = ({
  onSave,
  onSubmit,
  onAdequacaoSubmit,
  onBack,
  onCancel,
  onEdit,
  podeEditar = false,
  temAdequacao = false,
  showBack = false,
  showCancel = false,
  showEdit = false,
  showSave = true,
  showSubmit = true,
  submitLabel = "PRÓXIMO",
  submitDisabled = false,
  onlySubmit = false,
}: SaveSubmitButtonsProps) => {
  type PendingAction = "save" | "submit" | "adequacao";
  const [pending, setPending] = useState<PendingAction | null>(null);
  const isBusy = pending !== null;

  const [toastData, setToastData] = useState<{ show: boolean; message: string; type: string }>({
    show: false,
    message: "",
    type: "",
  });

  const applyFeedback = (
    toastResponse:
      | { show?: boolean; message?: string; type?: string; reload?: boolean }
      | void
      | null,
  ) => {
    if (!toastResponse || typeof toastResponse !== "object" || !toastResponse.show) {
      return;
    }

    if (toastResponse.reload) {
      setToastData((prev) => ({
        ...prev,
        show: true,
        message: toastResponse.message ?? "",
        type: toastResponse.type ?? "success",
      }));
      setTimeout(() => window.location.reload(), 500);
      return;
    }

    setToastData((prev) => ({ ...prev, ...toastResponse }));
  };

  const handleSave = async () => {
    if (!onSave || isBusy) return;
    setPending("save");
    try {
      const toastResponse = await onSave();
      applyFeedback(toastResponse);
    } finally {
      setPending(null);
    }
  };

  const handleSubmit = async () => {
    if (!onSubmit || isBusy) return;

    setPending("submit");
    try {
      const toastResponse = await onSubmit();
      applyFeedback(toastResponse);
    } finally {
      setPending(null);
    }
  };

  const handleAdequacao = async () => {
    if (!onAdequacaoSubmit || isBusy) return;
    setPending("adequacao");
    try {
      const toastResponse = await onAdequacaoSubmit();
      applyFeedback(toastResponse);
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="save-submit-buttons" aria-busy={isBusy}>
      {showBack && (
        <Button
          type="button"
          variant="outline-primary"
          onClick={onBack}
          className="back-btn"
          disabled={isBusy}
        >
          VOLTAR
        </Button>
      )}

      {showCancel && (
        <Button
          type="button"
          variant="danger"
          onClick={onCancel}
          className="cancel-btn"
          disabled={isBusy}
        >
          CANCELAR
        </Button>
      )}

      {showEdit && (
        <Button
          type="button"
          variant="info"
          onClick={onEdit}
          className="edit-btn"
          disabled={isBusy}
        >
          EDITAR
        </Button>
      )}

      {!onlySubmit && showSave && (
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          className="save"
          disabled={isBusy}
        >
          {pending === "save" ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                role="status"
                className="me-2 save-submit-buttons__spinner"
                aria-hidden
              />
              SALVANDO…
            </>
          ) : (
            "SALVAR"
          )}
        </Button>
      )}

      {temAdequacao && !onlySubmit && (
        <Button
          type="button"
          variant="warning"
          onClick={handleAdequacao}
          className="submit-btn"
          style={{ marginLeft: 8, marginRight: 8 }}
          disabled={isBusy}
        >
          {pending === "adequacao" ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                role="status"
                className="me-2 save-submit-buttons__spinner save-submit-buttons__spinner--dark"
                aria-hidden
              />
              ENVIANDO…
            </>
          ) : (
            "ENVIAR ADEQUAÇÃO"
          )}
        </Button>
      )}

      {showSubmit && (
        <Button
          type="button"
          variant="primary"
          onClick={handleSubmit}
          className="submit-btn"
          disabled={isBusy || submitDisabled}
        >
          {pending === "submit" ? (
            <>
              <Spinner
                animation="border"
                size="sm"
                role="status"
                className="me-2 save-submit-buttons__spinner"
                aria-hidden
              />
              AGUARDE…
            </>
          ) : (
            submitLabel
          )}
        </Button>
      )}

      <NotificationToast
        show={toastData.show}
        onClose={() => setToastData((prev) => ({ ...prev, show: false }))}
        message={toastData.message}
        type={toastData.type}
      />
    </div>
  );
};

export default SaveSubmitButtons;
